import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SageStatusBadge } from "@/components/SageStatusBadge";
import { Boxes, ShoppingBag, UserCheck, AlertTriangle, ArrowRight, PackageX } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-[#D4AF37]"}`} />
      </div>
      <div className="mt-3 text-3xl font-bold text-slate-50">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#94A3B8]">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [sage, setSage] = useState(null);
  const [inv, setInv] = useState([]);
  const [orders, setOrders] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/admin/sage/status").then(({ data }) => setSage(data));
      api.get("/admin/inventory").then(({ data }) => setInv(data));
      api.get("/admin/orders").then(({ data }) => setOrders(data));
      api.get("/admin/applications", { params: { status: "pending" } }).then(({ data }) => setApps(data));
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const lowOut = inv.filter((i) => i.stock_status !== "IN STOCK").length;
  const attention = orders.filter((o) => o.sage_status === "REQUIRES ATTENTION").length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-50">Operations Dashboard</h1>
          <p className="mt-1 text-sm text-[#94A3B8]">Live overview of Sage integration, stock and orders.</p>
        </div>
        {sage && (
          <Link to="/admin/sage"><SageStatusBadge status={sage.status} size="lg" testId="dashboard-sage-badge" /></Link>
        )}
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="stat-products" label="SKUs Tracked" value={inv.length} sub="in inventory monitor" icon={Boxes} />
        <Stat testId="stat-low-stock" label="Low / Out of Stock" value={lowOut} sub="need attention" icon={PackageX} accent="text-amber-400" />
        <Stat testId="stat-orders" label="Total Orders" value={orders.length} sub="all time" icon={ShoppingBag} />
        <Stat testId="stat-pending-apps" label="Pending Applications" value={apps.length} sub="awaiting approval" icon={UserCheck} accent="text-[#D4AF37]" />
      </div>

      {sage && (
        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-100">Sage 50 Integration Health</h3>
              <Link to="/admin/sage" className="inline-flex items-center gap-1 text-sm text-[#D4AF37]">Manage <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {[["Pending Orders", sage.pending_orders], ["Failed Jobs", sage.failed_jobs], ["Stock Discrepancies", sage.stock_discrepancies]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
                  <div className="text-xs text-[#64748B]">{k}</div>
                  <div className="mt-1 text-2xl font-bold text-slate-100">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
            <h3 className="text-sm font-semibold text-slate-100">Recent Orders</h3>
            <div className="mt-3 space-y-2">
              {orders.slice(0, 5).map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm">
                  <span className="font-mono-data text-[#D4AF37]">{o.order_number}</span>
                  <span className="text-[#94A3B8]">€{o.total.toFixed(2)}</span>
                </div>
              ))}
              {orders.length === 0 && <div className="text-sm text-[#64748B]">No orders yet.</div>}
            </div>
          </div>
        </div>
      )}

      {attention > 0 && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5 text-sm text-rose-300">
          <AlertTriangle className="h-5 w-5" /> {attention} order(s) require attention — Sage export failed after retries.
          <Link to="/admin/sage" className="ml-auto font-semibold text-rose-200 underline">Resolve</Link>
        </div>
      )}
    </div>
  );
}
